import React, { Fragment, useState, useEffect, forwardRef, useRef, useMemo } from 'react';
import RGL, { WidthProvider } from 'react-grid-layout';
import { Checkbox, Col, Radio, Row } from 'antd';
import { convertFromRaw, ContentState } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import PropTypes from 'prop-types';
import ReactDOMServer from 'react-dom/server';
import _ from 'lodash';
import { PhotoAlbum } from 'react-photo-album';

import Input from 'components/Input';
import TeXHTMLString from 'pages/Proposal/components/RichEditor/components/TeX/TeXHTMLString';
import TestimonialPublish from 'pages/Proposal/components/RichEditor/components/TestimonialComponent/TestimonialPublish';
import { WixPriceMilesPublish } from 'pages/Proposal/components/RichEditor/wix-components';
import blockStyle from 'pages/Proposal/components/Publish/helpers/blockStyle';
import proposalHelpers from 'helpers/proposal';
import helpers from 'helpers';
import useWindowDimensions from 'hooks/useWindowDimensions';
import { useWidth } from 'hooks/useWidth';
import { PROPOSAL_SUBSCRIPTION_TOPIC } from 'constants/index';
import { fontListEnglish, fontListHebrew } from 'pages/Proposal/constants/constants';
import { userVar } from 'graphql/cache';
import { useReactiveVar } from '@apollo/client';
import utils from 'utils/utils';
import { getShadow, getBorder } from 'utils/colors';
import { sanitizeObjectDeep } from 'utils/xss';
import { splitRawByAtomic } from 'pages/Proposal/components/Draft/utils/utils';

import './PublishSection.scss';

const ReactGridLayout = WidthProvider(RGL);

const findSimpleSectionLayOut = ({ position, proposalTextCell, spacing }) => {
  const simpleSectionLayout = {
    simpleSectionTitleSpan: 8,
    simpleSectionItemsSpan: 16,
    sectionContainerStyle: {},
    headerStyle: {},
    proposalTextCell,
    spacing,
  };

  if (!position) return simpleSectionLayout;
  if (position === 'top') {
    simpleSectionLayout.sectionContainerStyle = {
      // paddingBottom: '30px',
      display: 'flex',
    };
    simpleSectionLayout.headerStyle = {
      width: '100%',
    };
    simpleSectionLayout.simpleSectionTitleSpan = 24;
    simpleSectionLayout.simpleSectionItemsSpan = 24;
  } else if (position === 'top-middle') {
    simpleSectionLayout.sectionContainerStyle = {
      display: 'flex',
    };
    simpleSectionLayout.headerStyle = {
      width: '100%',
      justifyContent: 'center',
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
    };
    simpleSectionLayout.simpleSectionTitleSpan = 24;
    simpleSectionLayout.simpleSectionItemsSpan = 24;
  } else if (position === 'none') {
    simpleSectionLayout.headerStyle = {
      display: 'none',
    };
    simpleSectionLayout.simpleSectionTitleSpan = 24;
    simpleSectionLayout.simpleSectionItemsSpan = 24;
  } else if (position === 'large') {
    simpleSectionLayout.simpleSectionTitleSpan = 4;
    simpleSectionLayout.simpleSectionItemsSpan = 20;
  } else if (spacing) {
    simpleSectionLayout.spacing = {
      padding: spacing,
    };
  }
  return simpleSectionLayout;
};

const PublishSection = forwardRef((props, ref) => {
  const { isMobile: _isMobile, width: screenWidth } = useWindowDimensions();

  const {
    name,
    prop,
    configText,
    ispdf,
    updateProposalClient,
    showValidationError,
    setIsSavingForm,
    proposalSettings,
    screen,
    wixPreview,
    clientWixPreview,
    templateWixPreview,
    userWixPreview,
  } = props;

  const user = useReactiveVar(userVar);
  const variables = user?.adminFeatureFlags?.find((flag) => flag.key === 'HTML_IN_VARIABLES')
    ?.enabled
    ? prop?.draft?.variables
    : sanitizeObjectDeep(prop?.draft?.variables);
  const path = window.location.pathname;
  const isMobile = _isMobile || screen === 'mobile';
  const isWix =
    prop?.wixProposalId?.length ||
    wixPreview ||
    clientWixPreview ||
    templateWixPreview ||
    userWixPreview;
  const { dateFormat } = proposalSettings || {};

  const sectionLoaded = useRef(null);
  const formEditing = useRef(null);
  const rowRef = useRef(null);
  const rowWidth = useWidth(rowRef, screen);

  const titleRef = useRef(null);
  const titleWidth = useWidth(titleRef, screen);

  const sectionRef = useRef(prop.draft[name]);
  const styleRef = useRef(null);
  const focusedInputRef = useRef('');
  const [section, setSection] = useState('');
  // when editing prevent section update
  const [edited, setEdited] = useState(false);
  const [fonts, setFonts] = useState({});
  const [imagePreviewVisible, setImagePreviewVisible] = useState(false);

  useEffect(() => {
    if (edited || formEditing.current || sectionLoaded.current) {
      return;
    }

    sectionLoaded.current = true;
    setSection(prop.draft[name]);
    sectionRef.current = prop.draft[name];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, prop.draft]);

  const setSectionRef = (data) => {
    focusedInputRef.current = '';
    sectionRef.current = data;
    // setSection(data);
  };

  const getBody =
    prop.draft.bodyFont && prop.draft.bodyFont.fontSize ? prop.draft.bodyFont.fontSize : false;
  const titlePosition =
    section?.titlePosition ||
    (prop.draft.titleFont && prop.draft.titleFont.position ? prop.draft.titleFont.position : false);
  const spacing =
    section?.spacing ||
    (prop.draft.layoutConfig && prop.draft.layoutConfig.spacing
      ? prop.draft.layoutConfig.spacing
      : false);

  let fontSizeValue = 0;
  if (getBody) [fontSizeValue] = getBody.match(/\d+/);

  let titlehtml =
    templateWixPreview && name === 'milestones2' ? configText('payment terms') : section?.title;
  if (section?.rawtitle) {
    const titlecs = convertFromRaw(section.rawtitle);
    titlehtml =
      templateWixPreview && name === 'milestones2'
        ? configText('payment terms')
        : stateToHTML(titlecs, blockStyle({ language: prop.language }));
  }

  let subtitlehtml = section?.subtitle ? section.subtitle : null;
  if (section?.subrawtitle) {
    const subtitlecs = convertFromRaw(section.subrawtitle);
    subtitlehtml = stateToHTML(subtitlecs, blockStyle({ language: prop.language }));
  }

  // NO NEED to focus form input text
  // useEffect(() => {
  //   if (focusedInputRef.current) {
  //     const txt = document.getElementById(focusedInputRef.current);
  //     txt?.focus();
  //     var length = txt?.value?.length;
  //     txt?.setSelectionRange(0, length);
  //   }
  // }, [focusedInputRef.current]); // eslint-disable-line

  const saveProposal = (updatedSection, prop) => {
    // prevent saving if user is editing a form field
    if (focusedInputRef.current) {
      return;
    }

    let proposalInfo = {
      id: prop._id,
      _id: prop._id,
      pid: prop.pid,
      uid: prop.uid,
    };

    proposalInfo = Object.assign(proposalInfo, {
      draft: _.extend(prop.draft, updatedSection),
    });

    updateProposalClient({
      notifyOnNetworkStatusChange: true,
      variables: {
        topic: `${PROPOSAL_SUBSCRIPTION_TOPIC}_${
          proposalInfo?.channel || proposalInfo?.auid || proposalInfo?.uid
        }`,
        updateType: 'edit',
        proposal: proposalInfo,
      },
    }).then(
      () => setIsSavingForm(false),
      () => setIsSavingForm(false)
    );
  };

  const debouncedSave = useRef(_.debounce(saveProposal, 1000)).current;

  const handleFormEdit = (e) => {
    if (e.target.className.match('formfieldpublish')) {
      const [elementId, blockIndex, sectionName] = e.target?.id?.split('-');
      const entityMapBlock = sectionRef?.current?.raw?.entityMap[blockIndex];
      const formData = entityMapBlock && entityMapBlock?.data?.data;
      const atomicType = entityMapBlock?.type;

      if (atomicType !== 'form' || !formData || name !== sectionName.replaceAll('&hyphen&', '-')) {
        return;
      }
      setIsSavingForm(true);

      const tempFormData = formData.map((form) => {
        if (form?.key === elementId.replaceAll('&space&', ' ').replaceAll('&hyphen&', '-')) {
          form.value = e.target.value?.trim();
          if (form.required) {
            form.isFilled = true;
          }
        }
        return form;
      });

      let { entityMap } = JSON.parse(JSON.stringify(sectionRef?.current.raw));

      entityMap[blockIndex].data.data = tempFormData;

      const newSection = _.extend(sectionRef?.current, {
        raw: {
          blocks: sectionRef?.current.raw.blocks,
          entityMap,
        },
      });

      let updatedSection = {
        [name]: newSection,
      };

      sectionRef.current = { ...newSection };
      setTimeout(() => {
        saveProposal(updatedSection, prop);
      }, 5000);
      setSectionRef(updatedSection[name]);
    }
  };

  const handleFormFocusIn = (e) => {
    if (e.target.className === 'priceTableCheckbox') {
      // handleCheckboxEdit(e);
      return;
    }

    if (e.target.className.match('formfieldpublish')) {
      focusedInputRef.current = e.target?.id;
      formEditing.current = true;
    } else {
      focusedInputRef.current = '';
    }
  };

  const handleCheckboxEdit = (e) => {
    if (e.target.className === 'priceTableCheckbox') {
      const ids = e.target.id?.split('-');
      const optionIndex = parseInt(e.target['value']) || 0;
      const tempIds = [...ids];
      const tempSectionName =
        ids?.length > 8 ? tempIds.splice(ids?.length - 5)?.join('-') : ids[ids?.length - 1];

      if (name === tempSectionName) {
        const blockIndex = ids?.length > 8 ? ids[ids?.length - 7] : ids[ids?.length - 3];
        const selectedItemId = tempIds.slice(0, 5)?.join('-');

        const tempSection = sectionRef?.current;
        const content =
          tempSection?.raw?.entityMap[blockIndex] &&
          tempSection?.raw?.entityMap[blockIndex]?.data?.texcontent;
        const atomicType = tempSection?.raw?.entityMap[blockIndex]?.type;

        if (content && atomicType === 'TOKEN') {
          let json;
          try {
            json = JSON.parse(content);
          } catch (exx) {
            return '[pricing block]';
          }

          if (json?.deliverables) {
            let { entityMap } = JSON.parse(JSON.stringify(tempSection.raw));

            const keys = Object.keys(json.deliverables.chosen);
            json.deliverables.chosen[keys[optionIndex]].list = json.deliverables.chosen[
              keys[optionIndex]
            ].list.map((item) => {
              if (item.id === selectedItemId) {
                item.checked = e.target.checked;
              }
              return item;
            });

            entityMap[blockIndex].data.texcontent = JSON.stringify(json);

            let updatedSection = {
              [name]: _.extend(tempSection, {
                raw: {
                  blocks: tempSection.raw.blocks,
                  entityMap,
                },
              }),
            };

            // prevent section update for 60 sec
            setEdited(true);
            setTimeout(() => setEdited(false), 60000);

            // updating proposal, so prevent any other simultaneous updates
            setIsSavingForm(true);
            debouncedSave(updatedSection, prop);
            setSectionRef(updatedSection[name]);
          }
        }
      }
    }

    if (e.target?.labels?.[0]?.className?.includes('formfieldpublish')) {
      handleFormRadioCheckboxEdit(e);
    }
  };

  const handleFormRadioCheckboxEdit = (e) => {
    // Handle radio button and checkbox clicks in form elements
    if (['radio', 'checkbox'].includes(e.target.dataset.type || e.target.type)) {
      const ids = e.target.id?.split('-');
      if (ids && ids.length >= 3) {
        const blockIndex = ids[ids.length - 5];
        const sectionName = ids[ids.length - 4]?.replaceAll('&hyphen&', '-');
        const elementIndx = ids[ids.length - 3]?.replaceAll('&hyphen&', '-');
        // eslint-disable-next-line prefer-destructuring
        const rawKey = ids[5];
        const optionIndex = parseInt(ids[ids.length - 2]) || 0;

        
        if (name === sectionName) {
          const tempSection = sectionRef?.current;
          const formData =
            tempSection?.[rawKey]?.entityMap[blockIndex] &&
            tempSection?.[rawKey]?.entityMap[blockIndex]?.data?.data;
          const atomicType = tempSection?.[rawKey]?.entityMap[blockIndex]?.type;

          if (atomicType === 'form' && formData) {
            let { entityMap } = JSON.parse(JSON.stringify(tempSection[rawKey]));
            const formElement = formData[elementIndx];

            if (formElement.type === 'radio') {
              formElement.option = formElement.options.map((option, index) => {
                if (index === optionIndex) {
                  option.checked = true;
                } else {
                  option.checked = false;
                }
                return option;
              });
            } else if (formElement.type === 'checkbox') {
              formElement.options[optionIndex].checked =
                e.target.dataset.checked !== undefined
                  ? e.target.dataset.checked === 'false'
                    ? false
                    : true
                  : e.target.checked;
            }

            formData[elementIndx] = formElement;
            entityMap[blockIndex].data.data = formData;

            let updatedSection = {
              [name]: _.extend(tempSection, {
                [rawKey]: {
                  blocks: tempSection[rawKey].blocks,
                  entityMap,
                },
              }),
            };

            // prevent section update for 60 sec
            setEdited(true);
            setTimeout(() => setEdited(false), 60000);

            // updating proposal, so prevent any other simultaneous updates
            setIsSavingForm(true);
            debouncedSave(updatedSection, prop);
            setSectionRef(updatedSection[name]);
          }
        }
      }
    }
  };

  const handleImageEdit = (e) => {
    if (
      e.target.className?.includes?.('price-image-container') ||
      e.target.className?.includes?.('price-image-preview')
    ) {
      const imageId = e.target.id;
      if (imageId && window[`${imageId}`]?.images?.length > 0) {
        window.openImagePreview(window[`${imageId}`].images, 0, imageId);
      }
    }
  };

  const isSigned = props.prop?.signature?.date && props.prop?.signature?.date.trim() !== '';

  useEffect(() => {
    if (window.location.href.indexOf('/cd/') !== -1 || clientWixPreview) {
      document.addEventListener('focusout', handleFormEdit);
      document.addEventListener('focusin', handleFormFocusIn);
    }
    return () => {
      document.removeEventListener('focusout', handleFormEdit);
      document.removeEventListener('focusin', handleFormFocusIn);
    };
  }, []); //eslint-disable-line

  useEffect(() => {
    if (window.location.href.indexOf('/cd/') !== -1 || clientWixPreview) {
      document.addEventListener('click', handleCheckboxEdit);
      document.addEventListener('click', handleFormRadioCheckboxEdit);
    }

    if (
      window.location.href.indexOf('/cd/') !== -1 ||
      window.location.href.indexOf('/pd/') !== -1 ||
      clientWixPreview
    ) {
      document.addEventListener('click', handleImageEdit);
    }

    return () => {
      document.removeEventListener('click', handleCheckboxEdit);
      document.removeEventListener('click', handleImageEdit);
      document.removeEventListener('click', handleFormRadioCheckboxEdit);
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    if (styleRef.current && spacing) {
      styleRef.current.style.setProperty('--section-spacing', spacing);
    }
  }, [spacing]);

  const syncListMarkerStyles = () => {
    if (typeof window === 'undefined') {
      return;
    }

    const rootNode = styleRef?.current;

    if (!rootNode) {
      return;
    }

    const listItems = rootNode.querySelectorAll('ul li, ol li');

    listItems.forEach((item) => {
      const totalText = item.textContent?.trim() || '';

      if (!totalText) {
        item.style.removeProperty('--marker-color');
        item.style.removeProperty('--marker-size');
        return;
      }

      let commonColor = null;
      let commonFontSize = null;
      let totalColorStyledTextLength = 0;
      let totalFontSizeStyledTextLength = 0;
      let allSameColor = true;
      let allSameFontSize = true;

      const traverse = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent?.trim();
          if (text && text.length > 0) {
            let el = node.parentElement;
            let color = null;
            let fontSize = null;

            // Find closest styles
            while (el && el !== item && item.contains(el)) {
              if (!color && el.style?.color) {
                ({ color } = el.style);
              }
              if (!fontSize && el.style?.fontSize) {
                ({ fontSize } = el.style);
              }
              el = el.parentElement;
            }

            if (color) {
              totalColorStyledTextLength += text.length;
              if (commonColor === null) {
                commonColor = color;
              } else if (color !== commonColor) {
                allSameColor = false;
              }
            }

            if (fontSize) {
              totalFontSizeStyledTextLength += text.length;
              if (commonFontSize === null) {
                commonFontSize = fontSize;
              } else if (fontSize !== commonFontSize) {
                allSameFontSize = false;
              }
            }
          }
        } else {
          node.childNodes.forEach(traverse);
        }
      };

      traverse(item);

      const coverageThreshold = 0.95;
      const colorCoverageRatio =
        totalText.length > 0 ? totalColorStyledTextLength / totalText.length : 0;
      const fontSizeCoverageRatio =
        totalText.length > 0 ? totalFontSizeStyledTextLength / totalText.length : 0;

      if (allSameColor && commonColor && colorCoverageRatio >= coverageThreshold) {
        item.style.setProperty('--marker-color', commonColor);
      } else {
        item.style.removeProperty('--marker-color');
      }

      if (allSameFontSize && commonFontSize && fontSizeCoverageRatio >= coverageThreshold) {
        item.style.setProperty('--marker-size', commonFontSize);
      } else {
        item.style.removeProperty('--marker-size');
      }
    });
  };

  useEffect(() => {
    syncListMarkerStyles();
  });

  const proposalTextCell = proposalHelpers.getStyleObj(prop, 'body2', '', prop?.key);

  if (fontSizeValue && fontSizeValue <= 14) {
    proposalTextCell.marginTop = '10px';
    proposalTextCell.letterSpacing = '1px';
  }

  // const simpleSectionLayout = findSimpleSectionLayOut({
  //   position: titlePosition,
  //   proposalTextCell,
  // });
  const simpleSectionLayout = useMemo(
    () =>
      findSimpleSectionLayOut({
        position: titlePosition,
        spacing: spacing,
        proposalTextCell,
      }),
    [titlePosition, proposalTextCell, spacing]
  );

  const getComponentType = (texContent) => {
    const [contentType] = Object.keys(JSON.parse(texContent));
    return contentType;
  };

  const getFontDetail = (fontFamily) => {
    if (!fontFamily) {
      return null;
    }

    return (
      prop.language?.toLowerCase() === 'hebrew' || prop.language?.toLowerCase() === 'arabic'
        ? fontListHebrew
        : fontListEnglish
    ).find((item) => item.family === fontFamily);
  };

  const getSectionHtml = (raw, text, fullWidthImage, sectionSpan = 24, rawKey='raw') => {
    let width = 800;

    if (raw?.entityMap?.['0']?.type === 'gallery') {
      if (!rowWidth || !titleWidth) {
        return '<span></span>';
      }

      // if title and body is of same width, minus 0px from rowWidth
      const _titleWidth = titleWidth === rowWidth ? 0 : titleWidth;

      const factor = 24 / sectionSpan; // for multiple column
      // total width of the container of gallery (- 8 is for padding in multiple column)
      width = (rowWidth - _titleWidth) / factor - (sectionSpan === 24 ? 0 : 8);
    }

    let html = text;
    // eslint-disable-next-line no-empty
    if (raw) {
      const textcs = convertFromRaw(raw);
      html = stateToHTML(textcs, {
        blockRenderers: {
          atomic: (block) => {
            let data,
              type = undefined;

            try {
              const contentState = ContentState.createFromBlockArray([block]);
              const ent = block.getEntityAt(0);
              const entity = contentState.getEntity(ent);
              data = entity?.data;
              type = entity?.type;
            } catch (e) {
              // nothing in entityMap if block is atomic
              return null;
            }

            if (data && data.texcontent) {
              const blockKey = block.getKey();
              let blockIndex;

              raw?.blocks.forEach((b) => {
                if (b.key === blockKey) {
                  blockIndex = b.entityRanges?.[0]?.key;
                }
              });

              return ReactDOMServer.renderToStaticMarkup(
                <div className={`pricing-milestone-container ${getComponentType(data.texcontent)}`}>
                  {isWix ? (
                    <WixPriceMilesPublish
                      templateWixPreview={templateWixPreview}
                      prop={props.prop}
                      texcontent={data.texcontent}
                      wixPreview={wixPreview}
                      userWixPreview={userWixPreview}
                      configText={configText}
                    />
                  ) : (
                    <TeXHTMLString
                      content={data.texcontent}
                      configText={configText}
                      props={props}
                      blockIndex={blockIndex}
                      setImagePreviewVisible={setImagePreviewVisible}
                      imagePreviewVisible={imagePreviewVisible}
                    />
                  )}
                </div>
              );
            }

            if (data && data.data && type === 'table') {
              const tableData = data.data;
              const tableConfig = data.config;

              const list = tableData.map((p, rowIndex) => {
                return (
                  <tr
                    className="grey2"
                    key={p.id}
                    style={
                      rowIndex === 0
                        ? { backgroundColor: tableConfig?.topRowColor }
                        : { backgroundColor: tableConfig?.rowColor }
                    }>
                    {Object.keys(p)
                      .filter((k) => k !== 'id')
                      .map((k) => {
                        return (
                          <td className="grey1" key={`${p.id}${k}`}>
                            {p[k]}
                          </td>
                        );
                      })}
                  </tr>
                );
              });

              const tableHtml = () => (
                <figure className="table-content-container">
                  <table cellSpacing="3" style={{ align: 'center' }} className="table-container">
                    <tbody>{list}</tbody>
                  </table>
                </figure>
              );

              const tableStaticHtml = ReactDOMServer.renderToStaticMarkup(tableHtml());
              return tableStaticHtml;
            }

            if (data && data.data && type === 'testimonial') {
              const { data: testimonials, config } = data;

              const testimonialHtml = (
                <TestimonialPublish
                  bodyFont={props.prop?.draft?.bodyFont}
                  language={prop.language?.toLowerCase()}
                  testimonials={testimonials}
                  config={config}
                  fonts={fonts}
                  setFonts={setFonts}
                  getFontDetail={getFontDetail}
                />
              );

              return ReactDOMServer.renderToStaticMarkup(testimonialHtml);
            }

            if (data && data.data && type === 'form') {
              const formData = JSON.parse(JSON.stringify(data.data));
              const formConfig = data.config;
              const blockKey = block.getKey();
              let blockIndex;

              raw?.blocks.forEach((b) => {
                if (b.key === blockKey) {
                  blockIndex = b.entityRanges[0].key;
                }
              });
              let formTitle = formData.find((elem) => elem.head);
              if (formTitle) {
                if (formTitle.custom) {
                  formTitle = formTitle.key;
                } else {
                  formTitle = configText('Please fill the form below');
                }
              } else {
                formTitle = configText('Please fill the form below');
              }

              const formHtml = () => (
                <figure>
                  <div className="form-view">
                    <Row className="form-view-header">
                      <h3>{formTitle}</h3>
                    </Row>
                    <Row className="form-view-fields-row">
                      <div
                        id="form-list-items"
                        className={`form-list-items ${
                          formConfig.columns === 2 ? 'form-list-two-columns' : ''
                        }`}>
                        {_.map(
                          formData.filter((item) =>
                            ['radio', 'checkbox'].includes(item.type)
                              ? item?.options?.filter((option) => !!option.label)?.length > 0
                              : true
                          ),
                          (item, listIndex) =>
                            item.selected ? (
                              <div
                                key={`${item.key}`}
                                // onClick={() => handleItem(listIndex, item.selected ? false : true)}
                                className={
                                  item.selected ? 'form-list-item selected' : 'form-list-item'
                                }>
                                <label className="form-item-title">
                                  {configText(item.key)}{' '}
                                  {item.required ? <span className="required">*</span> : null}
                                </label>{' '}
                                {!['radio', 'checkbox'].includes(item.type) && (
                                  <Input
                                    type="text"
                                    placeholder={
                                      item?.placeholder
                                        ? item.placeholder
                                        : `${configText('Enter', true)} ${configText(
                                            item.key,
                                            true
                                          )}`
                                    }
                                    id={`${item?.key
                                      ?.replaceAll(' ', '&space&')
                                      ?.replaceAll(
                                        '-',
                                        '&hyphen&'
                                      )}-${blockIndex}-${name?.replaceAll('-', '&hyphen&')}-${rawKey}`}
                                    className={`formfieldpublish ${
                                      item.required && item.value === '' && showValidationError
                                        ? 'error-input'
                                        : ''
                                    }`}
                                    value={item.value}
                                    disabled={
                                      window.location.href.indexOf('/cd/') === -1 &&
                                      window.location.href.indexOf('/PDF/') === -1 &&
                                      !clientWixPreview
                                        ? true
                                        : false || !!isSigned
                                    }
                                  />
                                )}
                                {item.type === 'radio' && item?.options?.length > 0 && (
                                  <div className="radio-group-publish-form">
                                    {item?.options
                                      ?.filter((option) => !!option.label)
                                      ?.map((option, index) => (
                                        <Row>
                                          <Radio
                                            checked={option.checked}
                                            value={index}
                                            key={index}
                                            className="formfieldpublish"
                                            disabled={
                                              window.location.href.indexOf('/cd/') === -1 &&
                                              window.location.href.indexOf('/PDF/') === -1 &&
                                              !clientWixPreview
                                                ? true
                                                : false || !!isSigned
                                            }
                                            id={`${item?.key
                                              ?.replaceAll(' ', '&space&')
                                              ?.replaceAll(
                                                '-',
                                                '&hyphen&'
                                              )}-${blockIndex}-${name?.replaceAll(
                                              '-',
                                              '&hyphen&'
                                            )}-${listIndex}-${index}-${rawKey}`}></Radio>
                                          <span
                                            className="option-label"
                                            data-type={item.type}
                                            id={`${item?.key
                                              ?.replaceAll(' ', '&space&')
                                              ?.replaceAll(
                                                '-',
                                                '&hyphen&'
                                              )}-${blockIndex}-${name?.replaceAll(
                                              '-',
                                              '&hyphen&'
                                            )}-${listIndex}-${index}-${rawKey}`}>
                                            {option.label}
                                          </span>
                                        </Row>
                                      ))}
                                    {item.required &&
                                    ['radio', 'checkbox'].includes(item.type) &&
                                    item.options?.filter((option) => option.checked)?.length ===
                                      0 &&
                                    showValidationError ? (
                                      <span
                                        className="form-error-text"
                                        style={{ paddingLeft: '20px' }}>
                                        {configText('Please select option')}
                                      </span>
                                    ) : null}
                                  </div>
                                )}
                                {item.type === 'checkbox' && item?.options?.length > 0 && (
                                  <div className="checkbox-group-publish-form">
                                    {item?.options
                                      ?.filter((option) => !!option.label)
                                      ?.map((option, index) => (
                                        <Row>
                                          <Checkbox
                                            checked={option.checked}
                                            key={index}
                                            className="formfieldpublish"
                                            id={`${item?.key
                                              ?.replaceAll(' ', '&space&')
                                              ?.replaceAll(
                                                '-',
                                                '&hyphen&'
                                              )}-${blockIndex}-${name?.replaceAll(
                                              '-',
                                              '&hyphen&'
                                            )}-${listIndex}-${index}-${rawKey}`}
                                            disabled={
                                              window.location.href.indexOf('/cd/') === -1 &&
                                              window.location.href.indexOf('/PDF/') === -1 &&
                                              !clientWixPreview
                                                ? true
                                                : false || !!isSigned
                                            }></Checkbox>
                                          <span
                                            className="option-label"
                                            data-type={item.type}
                                            data-checked={!option.checked}
                                            id={`${item?.key
                                              ?.replaceAll(' ', '&space&')
                                              ?.replaceAll(
                                                '-',
                                                '&hyphen&'
                                              )}-${blockIndex}-${name?.replaceAll(
                                              '-',
                                              '&hyphen&'
                                            )}-${listIndex}-${index}-${rawKey}`}>
                                            {option.label}
                                          </span>
                                        </Row>
                                      ))}
                                    {item.required &&
                                    ['radio', 'checkbox'].includes(item.type) &&
                                    item.options?.filter((option) => option.checked)?.length ===
                                      0 &&
                                    showValidationError ? (
                                      <span
                                        className="form-error-text"
                                        style={{ paddingLeft: '20px' }}>
                                        {configText('Please select option')}
                                      </span>
                                    ) : null}
                                  </div>
                                )}
                                {item.required &&
                                !['radio', 'checkbox'].includes(item?.type) &&
                                item?.value === '' &&
                                showValidationError ? (
                                  <span className="form-error-text">
                                    {configText('Please enter')} {configText(item.key)}
                                  </span>
                                ) : null}
                              </div>
                            ) : (
                              <Fragment key={listIndex}></Fragment>
                            )
                        )}
                      </div>
                    </Row>
                  </div>
                </figure>
              );

              const formStaticHtml = ReactDOMServer.renderToStaticMarkup(formHtml());
              return formStaticHtml;
            }

            if (data && data.data && type === 'gallery') {
              const photos = utils.albumImagesBuilder(data.data);

              // if new gallery, return new gallery html
              if (photos.length === 0 || (data.config && Object.keys(data.config).length > 0)) {
                const { options, containerProps, imageProps } = utils.albumConfig(
                  data?.config,
                  width
                );

                try {
                  return ReactDOMServer.renderToStaticMarkup(
                    <div className="gallery-container" style={{ width: 'calc(100% - 2.93px)' }}>
                      <PhotoAlbum
                        photos={photos}
                        {...options}
                        renderPhoto={utils.renderGalleryPhoto}
                        componentsProps={{
                          containerProps,
                          imageProps: {
                            ...imageProps,
                            ...(window.location.href.includes('PDF') ? { loading: 'eager' } : {}),
                          },
                        }}
                        defaultContainerWidth={width - 2.93 || 800}
                      />
                    </div>
                  );
                } catch (err) {
                  return '';
                }
              }

              /* ----------------- old gallery ----------------- */
              /* 
                this is depricated and no longer supported in editor, only preview mode is working from July 2025.
                We can retire this component in the future (May be by July 2026).
              */
              const galleryImages = data.data;

              const galleryHtml = () => (
                <ReactGridLayout
                  className="layout"
                  height={150}
                  width={1200}
                  rowHeight={30}
                  margin={[2, 2]}
                  preventCollision={false}
                  isDraggable={false}
                  isResizable={false}
                  style={galleryImages.length ? {} : { display: 'none' }}>
                  {galleryImages.map((galleryImg) => (
                    <div
                      key={galleryImg.i}
                      style={{
                        position: 'initial',
                        backgroundImage: `url("${helpers.formatS3Link(galleryImg?.image)}")`,
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: '50% 50%',
                      }}
                      data-grid={{
                        i: galleryImg.i,
                        x: galleryImg.x,
                        y: galleryImg.y,
                        w: galleryImg.w,
                        h: galleryImg.h,
                      }}
                    />
                  ))}
                </ReactGridLayout>
              );

              const galleryStaticHtml = ReactDOMServer.renderToStaticMarkup(galleryHtml());
              return galleryStaticHtml;
              /* ----------------- END of old gallery ----------------- */
            }

            if (data && data.src && (type === 'image' || type === 'IMAGE')) {
              const imgContent = helpers.formatS3Link(data?.src);
              const imgWidth =
                (data?.config?.size?.width && window?.screen?.width > 480) ||
                (window?.screen?.width < 480 && data?.config?.size?.width < 360)
                  ? `${data?.config?.size?.width}px`
                  : '100%';
              const imgHeight = data?.config?.size?.height
                ? `${data?.config?.size?.height}`
                : '100%';
              const imgShadow = data?.config?.shadow;
              const imgBorder = data?.config?.border;
              const { opacity } = data?.config || {};

              const imgBoxShadow = getShadow(imgShadow);
              const imgBorderStyle = getBorder(imgBorder);
              const imgBorderRadiusStyle = `${imgBorder?.borderRadius || 0}px`;

              let imageStyle = `box-shadow: ${imgBoxShadow}; border: ${imgBorderStyle}; border-radius: ${imgBorderRadiusStyle}; opacity: ${
                opacity ?? 1
              };`;

              if (imgHeight) {
                imageStyle =
                  imageStyle + `aspect-ratio: ${data.config?.size?.width || 900} / ${imgHeight};`; //`height:${imgHeight};`;
              }
              if (imgWidth && !fullWidthImage) {
                imageStyle = imageStyle + `width:${imgWidth};`;
              }

              imageStyle = imageStyle + `margin: 20px 0px 10px 0px;`;

              const imgTag = `<img alt=""  src="${imgContent}" style="${imageStyle}" />`;
              return `<figure class="image-figure"  style="text-align:${data?.config?.alignment};">
                       ${
                         data?.hyperlink
                           ? `<a  href="${data?.hyperlink}" onClick="(function(e){
                            e.preventDefault();
                            e.stopPropagation();
                            window.open('${data?.hyperlink}', '_blank').focus();
                            return false;
                        })(arguments[0]);return false;">
                        ${imgTag}
                      </a>`
                           : imgTag
                       }
                </figure>`;
            }

            if (data && data.htmlCode && type === 'html') {
              const { htmlCode, config } = data;
              const htmlWidth = `${config?.size?.width || 530}px` || 'auto';
              const htmlHeight = `${config?.size?.height || 300}px` || 'auto';

              return `<div class="html-content" style="height: ${htmlHeight};width: ${htmlWidth}">
                  <div>${htmlCode}</div>
                </div>`;
            }

            if (data && type === 'divider') {
              return `<div style="border: 1px solid;margin-top: 1em;"></div>`;
            }

            if (data && data.html) {
              if (!ispdf) {
                const videoHtml = data.html;
                const temp = document.createElement('div');
                temp.innerHTML = videoHtml;

                const theIframe = temp.firstChild;
                let aspect = 56.25;

                const width = Number(theIframe.width);
                const height = Number(theIframe.height);
                if (width && height) {
                  aspect = (height / width) * 100;
                }

                return `
                  <figure>
                    <div class="media-component" style="text-align:center">
                        <div class="embed-wrap" style="padding-bottom:${aspect}%;">${videoHtml}</div>
                    </div>
                  </figure>
                `;
              }

              return `
                  <figure>
                    <div class="media-component" style="text-align:center">
                        <img class="pdf-media-thumb" src="${data.thumbnail_url}" />
                        <div class="origlink">${data.original_link}</div>
                      </div>
                  </figure>
                `;
            }

            return null;
          },
        },
        ...blockStyle({ language: prop.language }),
      });
    }

    html = html?.replace(/<.?p.?><.?br.?><.?\/.?p.?>$/, '');

    html = html ? utils.changeVariable(variables, html, dateFormat) : html;

    return html;
  };

  const getSpan = (span, index) => {
    if (span) {
      return span;
    } else {
      if (section.numberOfColumns === 2) {
        if (section.typeOfColumns === 'two') {
          return 12;
        } else if (section.typeOfColumns === 'tworight') {
          if (index === 0) return 18;
          else return 6;
        } else if (section.typeOfColumns === 'twoleft') {
          if (index === 0) return 6;
          else return 18;
        }
      } else if (section.numberOfColumns === 3) {
        return 8;
      } else if (section.numberOfColumns === 4) {
        return 6;
      } else {
        24;
      }
    }
  };

  const mainSection = (raw, text, index, rawKey, span, style, className) => {
    const sectionStyle =
      style ||
      Object.assign(
        { ...(isMobile && { float: 'unset' }) },
        index === 0
          ? isMobile
            ? { paddingBottom: '8px' }
            : { paddingRight: '8px' }
          : index
          ? isMobile
            ? { paddingBottom: '8px' }
            : { paddingLeft: '8px' }
          : {}
      );
    const sectionSpan = screen === 'mobile' ? 24 : getSpan(span, index);

    if (!raw) {
      return null;
    }
    // split the raw into multiple raw objects at atomic blocks
    const raws = isMobile ? splitRawByAtomic(raw) : [raw];

    const divs = raws.map((raw, index) => {
      return (
        <div
          key={index}
          dangerouslySetInnerHTML={{
            __html: getSectionHtml(
              raw,
              text,
              false,
              className?.includes('multiple') ? sectionSpan : 24,
              rawKey
            ),
          }}
        />
      );
    });

    return (
      <Col key={index} span={sectionSpan} style={sectionStyle} className={className}>
        {divs}
      </Col>
    );
  };

  const renderMainSection = () => {
    if (section?.numberOfColumns && section?.numberOfColumns !== 1) {
      const sectionContentStyle = Object.assign(
        simpleSectionLayout.proposalTextCell,
        isMobile ? { flexDirection: 'column' } : {}
      );

      return (
        <Col
          span={simpleSectionLayout.simpleSectionItemsSpan}
          className={`simple-section-content ${isMobile ? 'column' : ''} ${
            screen !== 'mobile' && section.numberOfColumns > 1
              ? 'simple-section-content-multiple'
              : ''
          }`}
          style={sectionContentStyle}>
          {_.times(section.numberOfColumns, (index) =>
            mainSection(
              section[`${index === 0 ? 'raw' : `raw${index}`}`],
              section[`${index === 0 ? 'text' : `text${index}`}`],
              index,
              `raw${index === 0 ? '' : index}`
            )
          )}
        </Col>
      );
    } else {
      return mainSection(
        section?.raw,
        section?.text,
        0,
        'raw',
        simpleSectionLayout.simpleSectionItemsSpan,
        simpleSectionLayout.proposalTextCell,
        'simple-section-content'
      );
    }
  };

  const sectionPreviw = path.includes('/ps/');

  const showBackgroundColorOption = section?.image ? false : true;

  const sectionBackgroundColor = showBackgroundColorOption
    ? prop?.draft?.sectionStyles?.[name]?.backgroundColor ||
      prop?.draft?.[name]?.sectionStyles?.backgroundColor ||
      prop?.draft?.layoutConfig?.backgroundColor
    : '#ffffff';

  return (
    <nobreak
      is="separator"
      id={`section-${name}`}
      class={`section-wrapper nobreak-${prop.name} ${section?.image ? 'avoid-nobreak' : ''}`}
      ref={ref}>
      <div
        id={name}
        ref={styleRef}
        className={`simple-section ${sectionPreviw ? 'is-section-preview' : ''} ${
          section?.image ? 'proposalblock-image' : ''
        } ${titlePosition && `editor-layout-position-${titlePosition}`} publish-section-spacing`}
        style={{
          backgroundColor: sectionBackgroundColor,
          ...(fontSizeValue && fontSizeValue <= 14 ? { padding: '20px 50px 0px' } : {}),
        }}>
        {section?.image ? (
          <div className="simple-section-container">
            <div className="w-row">
              <div
                className="danger-div image-publish-container simple-section-content"
                style={{ width: '100%' }}
                dangerouslySetInnerHTML={{ __html: getSectionHtml(section?.raw, null, true) }}
              />
            </div>
          </div>
        ) : (
          <Row
            className={`simple-section-container ${isMobile ? 'column-view' : ''}`}
            ref={rowRef}
            style={{
              ...simpleSectionLayout.sectionContainerStyle,
              ...(screenWidth < 768 && { flexFlow: 'column' }),
            }}>
            <Col
              ref={titleRef}
              span={simpleSectionLayout.simpleSectionTitleSpan}
              className="simple-section-title-block"
              style={simpleSectionLayout.headerStyle}>
              <div
                className="simple-section-title"
                style={proposalHelpers.getStyleObj(prop, 'title3', '', prop?.key)}
                dangerouslySetInnerHTML={{
                  __html: utils.changeVariable(variables, titlehtml, dateFormat),
                }}
              />

              <div
                className="simple-section-sub-title"
                style={proposalHelpers.getStyleObj(prop, 'title4', '', prop?.key)}
                dangerouslySetInnerHTML={{
                  __html: utils.changeVariable(variables, subtitlehtml, dateFormat),
                }}
              />
            </Col>
            {renderMainSection()}
          </Row>
        )}
      </div>
    </nobreak>
  );
});

PublishSection.propTypes = {
  name: PropTypes.string.isRequired,
  prop: PropTypes.instanceOf(Object).isRequired,
  configText: PropTypes.func.isRequired,
  ispdf: PropTypes.bool.isRequired,
  showValidationError: PropTypes.bool,
  screen: PropTypes.string,
  wixPreview: PropTypes.bool,
  clientWixPreview: PropTypes.bool,
  templateWixPreview: PropTypes.bool,
  userWixPreview: PropTypes.bool,
};

export default PublishSection;
